import { useEffect, useState } from 'react';
import {
  ETHNICITY_TYPE,
  GENDER_TYPE,
  paths,
  RACE_TYPE,
  RELATIONSHIP_TYPE,
  WILLINGNESS_TYPE,
} from '@common/types';
import { t } from 'i18next';
import { InferType } from 'yup';
import { addCaregiverSchema } from '@views/pages/Patient/components/CareTeam/components/schemas';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  useAddCaregiverMutation,
  usePatientCareTeamCaregiverQuery,
} from '@common/cache';
import { toast } from 'react-toastify';
import { formatApiError } from '@common/errors/error-formatting.ts';
import { fillAddressFormValues } from '@util/google-places';
import { isNil } from 'lodash';

type IFormShape = InferType<typeof addCaregiverSchema>;

const initialValues: Partial<IFormShape> = {
  first_name: '',
  middle_name: '',
  last_name: '',
  birthdate: '',
  gender: undefined,
  race: undefined,
  ethnicity: undefined,
  relationship: undefined,
  start_date: '',
  street: '',
  email: '',
  state: '',
  city: '',
  country: 'USA',
  zip_code: '',
  phone_number: '',
  primaryCaregiver: 'true',
  emergencyContact: 'false',
  powerOfAttorney: 'false',
  guardian: 'false',
  other: 'false',
};

const confirmationRadioButtonOptions = [
  { value: 'yes', label: 'Yes' },
  { value: 'no', label: 'No' },
];

//Did this because of the field value restrictions.
//Must urgently rework this to remove these confusing type casts.
const extractFieldValueFromBoolean = (value: boolean | undefined) => {
  if (value === undefined) return '';
  if (value === true) return 'yes';
  if (value === false) return 'no';
};

const extractBooleanFromFieldValue = (value: string | null | undefined) => {
  if (isNil(value)) return null;
  if (value === 'yes') return true;
  if (value === 'no') return false;
};

export const useEditCaregiverItem = (patientId: string) => {
  const { mutateAsync, isPending } = useAddCaregiverMutation(patientId);

  const { data } = usePatientCareTeamCaregiverQuery(patientId);
  const [isOpen, setIsOpen] = useState(false);
  const [isSaveChangesModalOpen, setIsSaveChangesModalOpen] = useState(false);

  const formMethods = useForm<IFormShape>({
    mode: 'onChange',
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resolver: yupResolver<IFormShape, any, any>(addCaregiverSchema),
    defaultValues: initialValues,
  });

  const {
    handleSubmit,
    formState: { isSubmitting, isValid, isDirty },
    reset,
    setValue,
  } = formMethods;

  const genderOptions = [
    { value: GENDER_TYPE.MALE, label: t('patients.addModal.gender.male') },
    { value: GENDER_TYPE.FEMALE, label: t('patients.addModal.gender.female') },
    {
      value: GENDER_TYPE.UNKNOWN,
      label: t('patients.addModal.gender.unknown'),
    },
  ];

  const raceOptions = [
    {
      value: 'American Indian or Alaska Native',
      label: t('common.race.alaska'),
    },
    { value: 'Asian', label: t('common.race.asian') },
    { value: 'Black or African American', label: t('common.race.black') },
    {
      value: 'Native Hawaiian or Other Pacific Islander',
      label: t('common.race.hawaiian'),
    },
    { value: 'White', label: t('common.race.white') },
    { value: 'Asked but unknown', label: t('common.race.asked') },
    { value: 'Other', label: t('common.race.other') },
    { value: 'Unknown', label: t('common.race.unknown') },
  ];

  const ethnicityOptions = [
    { value: 'Hispanic or Latino', label: t('common.ethnicity.hispanic') },
    {
      value: 'Not Hispanic or Latino',
      label: t('common.ethnicity.noHispanic'),
    },
    { value: 'Asked but unknown', label: t('common.ethnicity.asked') },
    { value: 'Unknown', label: t('common.ethnicity.unknown') },
  ];

  const relationshipOptions = [
    { value: 'Spouse', label: t('common.relationship.spouse') },
    {
      value: 'Domestic partner',
      label: t('common.relationship.domesticPartner'),
    },
    { value: 'Daughter', label: t('common.relationship.daughter') },
    { value: 'Son', label: t('common.relationship.son') },
    { value: 'Sibling', label: t('common.relationship.sibling') },
    {
      value: 'Other family member',
      label: t('common.relationship.otherFamilyMember'),
    },
    { value: 'Friend', label: t('common.relationship.friend') },
    {
      value: 'Other non-family member',
      label: t('common.relationship.otherNonFamilyMember'),
    },
  ];

  const phoneTypeOptions = [
    { value: 'Home', label: t('common.phoneType.home') },
    { value: 'Cell', label: t('common.phoneType.cell') },
    { value: 'Business', label: t('common.phoneType.business') },
  ];

  const willingnessOptions = [
    {
      value: 'willing to participate in the GUIDE program',
      label: t('common.willingness.willing'),
    },
    {
      value: 'not willing to participate in the GUIDE program',
      label: t('common.willingness.not_willing'),
    },
    { value: 'not available', label: t('common.willingness.not_available') },
  ];

  const roles: {
    [key: string]:
      | 'Primary caregiver'
      | 'Emergency contact'
      | 'Power of Attorney'
      | 'Guardian'
      | 'Other';
  } = {
    primaryCaregiver: 'Primary caregiver',
    emergencyContact: 'Emergency contact',
    powerOfAttorney: 'Power of Attorney',
    guardian: 'Guardian',
    other: 'Other',
  };

  useEffect(() => {
    if (data) {
      reset({
        ...data,
        ...data.address,
        primaryCaregiver: 'true',
        emergencyContact: data.roles.find(
          item => item === roles.emergencyContact,
        )
          ? 'true'
          : 'false',
        powerOfAttorney: data.roles.find(item => item === roles.powerOfAttorney)
          ? 'true'
          : 'false',
        guardian: data.roles.find(item => item === roles.guardian)
          ? 'true'
          : 'false',
        other: data.roles.find(item => item === roles.other) ? 'true' : 'false',
        lives_with_patient: extractFieldValueFromBoolean(
          data.lives_with_patient,
        ),
        has_medicare: extractFieldValueFromBoolean(data.has_medicare),
        gender: data.gender as GENDER_TYPE,
        race: data.race as RACE_TYPE,
        ethnicity: data.ethnicity as ETHNICITY_TYPE,
        relationship: data.relationship as RELATIONSHIP_TYPE,
        willingness: data.willingness as WILLINGNESS_TYPE,
      });
    }
  }, [data]);

  const handleClose = () => {
    if (isDirty) {
      setIsSaveChangesModalOpen(true);
    } else {
      setIsOpen(false);
    }
  };

  const handleOpen = () => {
    setIsOpen(true);
  };

  const handleSelectLocation = (place: Partial<google.maps.places.Place>) => {
    fillAddressFormValues({ setValue, place });
  };

  const submitHandler = async (values: IFormShape) => {
    const birthdate = values.birthdate ? new Date(values.birthdate) : null;
    const start_date = values.start_date ? new Date(values.start_date) : null;

    try {
      await mutateAsync({
        first_name: values.first_name,
        middle_name: values.middle_name,
        last_name: values.last_name,
        birthdate: birthdate ? birthdate.toISOString() : null,
        gender: values.gender,
        race: values.race,
        ethnicity: values.ethnicity,
        relationship:
          values.relationship as paths['/patient/{patient_id}/caregiver']['patch']['requestBody']['content']['application/json']['relationship'],
        lives_with_patient: extractBooleanFromFieldValue(
          values.lives_with_patient,
        ),
        has_medicare: extractBooleanFromFieldValue(values.has_medicare),
        start_date: start_date ? start_date.toISOString() : null,
        phone_number: values.phone_number,
        phone_type:
          values.phone_type as paths['/patient/{patient_id}/caregiver']['patch']['requestBody']['content']['application/json']['phone_type'],
        google_places_id: values.google_places_id,
        state: values.state,
        city: values.city,
        country: values.country,
        zip_code: values.zip_code,
        street: values.street,
        email: values.email,
        willingness: values.willingness,
        roles: [
          'Primary caregiver',
          ...(values.emergencyContact === 'true'
            ? [roles.emergencyContact]
            : []),
          ...(values.powerOfAttorney === 'true' ? [roles.powerOfAttorney] : []),
          ...(values.guardian === 'true' ? [roles.guardian] : []),
          ...(values.other === 'true' ? [roles.other] : []),
        ],
        ...(values.suite && { suite: values.suite }),
      });

      setIsSaveChangesModalOpen(false);
      setIsOpen(false);
      reset(values);
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const onSubmit = handleSubmit(submitHandler);

  const handleCloseSaveChangesModal = () => {
    setIsSaveChangesModalOpen(false);
  };

  const handleDiscardChanges = () => {
    setIsSaveChangesModalOpen(false);
    setIsOpen(false);
    reset();
  };

  const handleSaveChanges = async () => {
    await onSubmit();
  };

  return {
    isOpen,
    handleClose,
    handleOpen,
    isSubmitting,
    reset,
    genderOptions,
    raceOptions,
    ethnicityOptions,
    relationshipOptions,
    phoneTypeOptions,
    willingnessOptions,
    formMethods,
    onSubmit,
    handleSelectLocation,
    confirmationRadioButtonOptions,
    isSaveChangesModalOpen,
    handleCloseSaveChangesModal,
    handleSaveChanges,
    handleDiscardChanges,
    isValid,
    isEditSubmitting: isPending,
  };
};
